import { Avatar, AvatarFallback, AvatarImage } from '@crowdfpl/ui-web/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@crowdfpl/ui-web/card';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PinnedTrophyBadge } from '@/components/gamification/pinned-trophy-badge';
import { UsernameWithXLink } from '@/components/shared/UsernameWithXLink';
import { trackUmamiEvent } from '@/helpers/analytics';
import { formatCaptainLeaderboardStats } from '@/helpers/captain-leaderboard-stats';
import { getCountryFlag } from '@/helpers/countries';
import { LeaderboardEntry } from '@/hooks/data/leaderboard';

interface LeaderboardCardProps {
  entries: LeaderboardEntry[];
  title: string;
  /** @deprecated Use Link component internally instead */
  onUserClick?: (username: string) => void;
  showTitle?: boolean;
  currentUserId?: string | null;
  searchQuery?: string;
  /** Show rating_change instead of predictor_rating (for GW mode) */
  showRatingChange?: boolean;
  /** Captain scope: show captain stats instead of duels stats */
  captainMode?: boolean;
}

export function LeaderboardCard({
  entries,
  title,
  onUserClick,
  showTitle = true,
  currentUserId,
  showRatingChange = false,
  captainMode = false,
}: LeaderboardCardProps) {
  const shouldShowRankMovement = !showRatingChange;
  const getRankIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'text-yellow-600';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-amber-600';
    return 'text-muted-foreground';
  };

  const renderRankMovement = (movement?: number) => {
    if (movement == null) {
      return null;
    }
    const delta = movement;
    if (delta > 0) {
      return (
        <span className="inline-flex items-center gap-0.5 text-success">
          <TrendingUp className="h-3 w-3" />
          +{delta}
        </span>
      );
    }
    if (delta < 0) {
      return (
        <span className="inline-flex items-center gap-0.5 text-destructive">
          <TrendingDown className="h-3 w-3" />
          -{Math.abs(delta)}
        </span>
      );
    }
    return <span className="text-muted-foreground">= 0</span>;
  };

  const renderCaptainStats = (entry: LeaderboardEntry) => formatCaptainLeaderboardStats(entry);

  const renderDuelsStats = (entry: LeaderboardEntry) => {
    if (showRatingChange && entry.success_rate_gw !== undefined && entry.votes_in_gw !== undefined) {
      return <span>{entry.success_rate_gw.toFixed(1)}% · {entry.votes_in_gw} votes</span>;
    }
    return (
      <span className="inline-flex items-center gap-1.5">
        {shouldShowRankMovement && renderRankMovement(entry.rank_movement) ? (
          <>
            {renderRankMovement(entry.rank_movement)}
            <span>·</span>
          </>
        ) : null}
        <span>{entry.success_rate.toFixed(1)}% · {entry.total_predictions} votes</span>
      </span>
    );
  };

  const renderRating = (entry: LeaderboardEntry) => {
    if (showRatingChange && entry.rating_change !== undefined) {
      return (
        <span className={`text-[18px] font-bold tabular-nums leading-[1.1] tracking-[-0.3px] ${entry.rating_change > 0 ? 'text-rating-positive' : entry.rating_change < 0 ? 'text-destructive' : 'text-muted-foreground'
          }`}>
          {entry.rating_change > 0 ? '+' : ''}{entry.rating_change}
        </span>
      );
    }
    const ratingValue = captainMode && entry.captain_rating != null ? entry.captain_rating : entry.predictor_rating;
    return (
      <span className="text-[18px] font-bold text-rating-positive tabular-nums leading-[1.1] tracking-[-0.3px]">
        {ratingValue}
      </span>
    );
  };

  const renderMobileRating = (entry: LeaderboardEntry) => {
    if (showRatingChange && entry.rating_change !== undefined) {
      return (
        <span className={`text-[18px] font-bold tabular-nums leading-none tracking-[-0.3px] flex-shrink-0 ${entry.rating_change > 0 ? 'text-rating-positive' : entry.rating_change < 0 ? 'text-destructive' : 'text-muted-foreground'
          }`}>
          {entry.rating_change > 0 ? '+' : ''}{entry.rating_change}
        </span>
      );
    }
    const ratingValue = captainMode && entry.captain_rating != null ? entry.captain_rating : entry.predictor_rating;
    return (
      <span className="text-[18px] font-bold text-rating-positive tabular-nums leading-none tracking-[-0.3px] flex-shrink-0">
        {ratingValue}
      </span>
    );
  };

  return (
    <Card className="w-full bg-card border-border/30 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      {showTitle && (
        <CardHeader className="pb-2 pt-3">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="p-0">
        <div className="divide-y divide-border/20">
          {entries.map((entry) => {
            const isCurrentUser = currentUserId && entry.user_id === currentUserId;
            return (
              <Link
                key={entry.user_id}
                to={`/profile/${entry.username}`}
                onClick={() => {
                  trackUmamiEvent('leaderboard_profile_click', { username: entry.username, rank: entry.rank });
                  onUserClick?.(entry.username);
                }}
                className={`relative block transition-colors duration-150 ${isCurrentUser
                  ? 'bg-leaderboard-row dark:bg-[hsl(var(--primary)/0.06)]'
                  : 'hover:bg-muted/30'
                  }`}
              >
                {/* Desktop Layout */}
                <div className="hidden md:flex items-center gap-3 px-4 py-2.5">
                  {/* Rank */}
                  <div className="w-10 text-center flex-shrink-0">
                    {getRankIcon(entry.rank) ? (
                      <span className="text-lg leading-none">{getRankIcon(entry.rank)}</span>
                    ) : (
                      <span className={`text-sm font-medium leading-tight ${getRankColor(entry.rank)}`}>#{entry.rank}</span>
                    )}
                  </div>

                  {/* Avatar */}
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarImage src={entry.avatar_url} alt={entry.username} />
                    <AvatarFallback className="bg-muted text-foreground text-[13px] font-semibold w-full h-full flex items-center justify-center">
                      {entry.username.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  {/* Name + Flag + Badge */}
                  <div className={`flex-1 min-w-0 flex items-center gap-1.5 text-[14px] font-semibold leading-tight ${isCurrentUser ? 'text-primary' : 'text-foreground'}`}>
                    <div className="truncate">
                      <UsernameWithXLink
                        username={entry.username}
                        twitterUsername={entry.twitter_username}
                        showTwitterUsername={entry.show_twitter_username}
                        iconClassName="h-3 w-3"
                        insideLink
                        trackSource="leaderboard"
                      />
                    </div>
                    <PinnedTrophyBadge pinnedTrophy={entry.pinned_trophy} decorative />
                    {entry.country ? <span className="text-sm leading-none">{getCountryFlag(entry.country)}</span> : null}
                  </div>

                  {/* Stats */}
                  <div className="text-[13px] text-muted-foreground leading-tight flex-shrink-0">
                    {captainMode ? (
                      <span>{renderCaptainStats(entry)}</span>
                    ) : (
                      renderDuelsStats(entry)
                    )}
                  </div>

                  {/* Rating */}
                  <div className="w-16 text-right flex-shrink-0">
                    {renderRating(entry)}
                  </div>
                </div>

                {/* Mobile Layout - Two lines */}
                <div className="md:hidden px-3 py-1.5">
                  {/* Line 1 */}
                  <div className="flex items-center gap-2">
                    {/* Rank */}
                    <div className="w-6 text-center flex-shrink-0">
                      {getRankIcon(entry.rank) ? (
                        <span className="text-sm leading-none">{getRankIcon(entry.rank)}</span>
                      ) : (
                        <span className={`text-[11px] font-medium leading-tight ${getRankColor(entry.rank)}`}>
                          #{entry.rank}
                        </span>
                      )}
                    </div>

                    {/* Avatar */}
                    <Avatar className="h-7 w-7 flex-shrink-0">
                      <AvatarImage src={entry.avatar_url} alt={entry.username} />
                      <AvatarFallback className="bg-muted text-foreground text-[12px] font-semibold w-full h-full flex items-center justify-center">
                        {entry.username.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>

                    {/* Name + Flag + Badge */}
                    <div className={`flex-1 min-w-0 flex items-center gap-1 text-[13px] font-semibold leading-tight ${isCurrentUser ? 'text-primary' : 'text-foreground'}`}>
                      <div className="truncate">
                        <UsernameWithXLink
                          username={entry.username}
                          twitterUsername={entry.twitter_username}
                          showTwitterUsername={entry.show_twitter_username}
                          iconClassName="h-3 w-3"
                          insideLink
                          trackSource="leaderboard"
                        />
                      </div>
                      <PinnedTrophyBadge pinnedTrophy={entry.pinned_trophy} decorative />
                      {entry.country ? <span className="text-xs leading-none">{getCountryFlag(entry.country)}</span> : null}
                    </div>

                    {/* Rating */}
                    {renderMobileRating(entry)}
                  </div>

                  {/* Line 2 */}
                  <div className="mt-0.5 ml-[52px] text-[11px] text-muted-foreground leading-tight">
                    {captainMode ? (
                      <span>{renderCaptainStats(entry)}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        {shouldShowRankMovement && renderRankMovement(entry.rank_movement) ? (
                          <>
                            {renderRankMovement(entry.rank_movement)}
                            <span>·</span>
                          </>
                        ) : null}
                        {showRatingChange && entry.success_rate_gw !== undefined && entry.votes_in_gw !== undefined ? (
                          <span>{entry.success_rate_gw.toFixed(1)}% · {entry.votes_in_gw} votes</span>
                        ) : (
                          <span>{entry.success_rate.toFixed(1)}% · {entry.total_predictions} votes</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

